/**
 * Helpers for files fetched through the authenticated API client (responseType "blob"). A plain link
 * would not send the Authorization header, so the bytes are fetched first and then saved or opened here.
 */

/** The filename from a Content-Disposition header, or the fallback when it is missing or unreadable. */
export function filenameFromContentDisposition(header: string | null | undefined, fallback: string): string {
  if (!header) {
    return fallback;
  }
  const encoded = /filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i.exec(header);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1].trim().replace(/^"|"$/g, '')) || fallback;
    } catch {
      // fall through to the plain filename
    }
  }
  const plain = /filename\s*=\s*("([^"]+)"|[^;]+)/i.exec(header);
  const name = plain ? (plain[2] ?? plain[1]).trim() : '';
  return name || fallback;
}

/** Saves a blob to disk under the given filename. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();
  revokeLater(url);
}

/**
 * Opens a blob in a new tab. Returns false when the browser blocks the tab (it was not opened from the click
 * itself, so a pop-up blocker may refuse it); the caller can then offer the file as a download instead.
 */
export function openBlobInNewTab(blob: Blob): boolean {
  const url = URL.createObjectURL(blob);
  const tab = window.open(url, '_blank');
  if (!tab) {
    URL.revokeObjectURL(url);
    return false;
  }
  // The tab needs the URL for as long as it is open and loading, so it is released after a good while.
  revokeLater(url, 60_000);
  return true;
}

/**
 * The message from an API error. With responseType "blob" the JSON error body ({ status: false, message })
 * arrives as a Blob, so it has to be read first.
 */
export async function blobErrorMessage(error: unknown, fallback: string): Promise<string> {
  const body = (error as { error?: unknown } | null)?.error;
  try {
    const text = body instanceof Blob ? await body.text() : typeof body === 'string' ? body : null;
    if (text) {
      const message = JSON.parse(text)?.message;
      if (typeof message === 'string' && message.trim()) {
        return message;
      }
    } else if (body && typeof body === 'object') {
      const message = (body as { message?: unknown }).message;
      if (typeof message === 'string' && message.trim()) {
        return message;
      }
    }
  } catch {
    // not JSON: use the fallback
  }
  return fallback;
}

function revokeLater(url: string, delay = 10_000): void {
  setTimeout(() => URL.revokeObjectURL(url), delay);
}
